import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Linking,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { CustomAlert } from "../ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { parseCoordinatesOrUrl } from "common247";
import { getHighAccuracyLocation } from "@/services/media-capture";
import {
  checkLocationServicesEnabled,
  checkLocationPermissions,
  requestLocationPermissions,
} from "@/services/location-permissions";
import { GoogleMapsEmbed } from "./GoogleMapsEmbed";
import { ThemedToggle } from "@/components/ThemedToggle";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import {
  calculatePopoverPosition,
  type IPopoverAnchor,
  type IPopoverLayout,
} from "@/utils/popover-positioning";

/**
 * Result returned when a location is confirmed in the picker.
 */
export interface ISelectedLocationResult {
  location: string;
  latitude: number;
  longitude: number;
  isApproximate?: boolean;
}

/**
 * Props for the GoogleMapsLocationSelector component.
 */
export interface IGoogleMapsLocationSelectorProps {
  visible: boolean;
  onClose: () => void;
  initialLocation?: string;
  initialLatitude?: number;
  initialLongitude?: number;
  initialIsApproximate?: boolean;
  onSelect: (result: ISelectedLocationResult) => void;
  /**
   * Optional contextual anchor position (e.g. cursor or trigger coordinates).
   */
  anchorPosition?: IPopoverAnchor | null;
}

export type ILocationSelectorProps = IGoogleMapsLocationSelectorProps;

/**
 * OpenStreetMap Nominatim search result item.
 */
interface INominatimPlace {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
}

/**
 * Popup selector enabling interactive location selection via Google Maps embed,
 * place and address search, current device GPS acquisition, and coordinate entry.
 * Follows the standardized Popup UX pattern across the Memories platform.
 *
 * @param {IGoogleMapsLocationSelectorProps} props - Component props.
 * @returns {React.ReactElement} The rendered location selector popup.
 */
export function GoogleMapsLocationSelector({
  visible,
  onClose,
  initialLocation = "",
  initialLatitude,
  initialLongitude,
  initialIsApproximate = false,
  onSelect,
  anchorPosition,
}: IGoogleMapsLocationSelectorProps): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState("");
  const [locationName, setLocationName] = useState(initialLocation);
  const [latitude, setLatitude] = useState<number | null>(initialLatitude ?? null);
  const [longitude, setLongitude] = useState<number | null>(initialLongitude ?? null);
  const [isApproximate, setIsApproximate] = useState(Boolean(initialIsApproximate));
  const [latInput, setLatInput] = useState(initialLatitude != null ? String(initialLatitude) : "");
  const [lonInput, setLonInput] = useState(initialLongitude != null ? String(initialLongitude) : "");
  const [searching, setSearching] = useState(false);
  const [acquiringGps, setAcquiringGps] = useState(false);
  const [searchResults, setSearchResults] = useState<INominatimPlace[]>([]);

  const [layout, setLayout] = useState<IPopoverLayout>(() =>
    calculatePopoverPosition({
      anchorPosition,
      popoverWidth: 380,
      popoverHeight: 420,
    })
  );

  useEffect(() => {
    if (!visible) return;
    const updateLayout = () => {
      setLayout(
        calculatePopoverPosition({
          anchorPosition,
          popoverWidth: 380,
          popoverHeight: 420,
        })
      );
    };
    updateLayout();
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.addEventListener("resize", updateLayout);
      return () => {
        window.removeEventListener("resize", updateLayout);
      };
    }
  }, [visible, anchorPosition]);

  const cardBg = useThemeColor({}, "card");
  const screenBg = useThemeColor({}, "background");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  useEffect(() => {
    if (visible) {
      setLocationName(initialLocation || "");
      setLatitude(initialLatitude ?? null);
      setLongitude(initialLongitude ?? null);
      setIsApproximate(Boolean(initialIsApproximate));
      setLatInput(initialLatitude != null ? String(initialLatitude) : "");
      setLonInput(initialLongitude != null ? String(initialLongitude) : "");
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [visible, initialLocation, initialLatitude, initialLongitude, initialIsApproximate]);

  /**
   * Reverse geocode coordinates to find a friendly place name.
   */
  const reverseGeocode = async (lat: number, lon: number): Promise<string | null> => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`;
      const res = await fetch(url, {
        headers: { "User-Agent": "MemoriesApp/1.0" },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.display_name) {
          const parts = data.display_name.split(",");
          return parts.slice(0, 3).join(",").trim();
        }
      }
    } catch {
      // Fallback silently if offline or rate limited
    }
    return null;
  };

  /**
   * Perform place search or detect pasted coordinates/Google Maps URL.
   */
  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;

    // First check if it is a coordinate string or Google Maps URL
    const parsedCoords = parseCoordinatesOrUrl(q);
    if (parsedCoords) {
      setLatitude(parsedCoords.latitude);
      setLongitude(parsedCoords.longitude);
      setLatInput(String(parsedCoords.latitude));
      setLonInput(String(parsedCoords.longitude));
      setSearchResults([]);

      const friendlyName = await reverseGeocode(parsedCoords.latitude, parsedCoords.longitude);
      if (friendlyName) {
        setLocationName(friendlyName);
      } else if (!locationName) {
        setLocationName(`${parsedCoords.latitude.toFixed(4)}, ${parsedCoords.longitude.toFixed(4)}`);
      }
      return;
    }

    setSearching(true);
    setSearchResults([]);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        q
      )}&limit=5&addressdetails=1`;
      const res = await fetch(url, {
        headers: { "User-Agent": "MemoriesApp/1.0" },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setSearchResults(data);
        } else {
          CustomAlert.alert("No places found", "Please check the spelling or enter coordinates directly.");
        }
      } else {
        CustomAlert.alert("Search unavailable", "Could not reach place search service.");
      }
    } catch (e: any) {
      CustomAlert.alert("Search error", e.message || "Failed to search location");
    } finally {
      setSearching(false);
    }
  };

  /**
   * Select a search result place.
   */
  const handleSelectPlace = (place: INominatimPlace) => {
    const lat = parseFloat(place.lat);
    const lon = parseFloat(place.lon);
    setLatitude(lat);
    setLongitude(lon);
    setLatInput(String(lat));
    setLonInput(String(lon));

    const parts = place.display_name.split(",");
    const shortLabel = parts.slice(0, 3).join(",").trim();
    setLocationName(shortLabel);
    setSearchResults([]);
  };

  /**
   * Fetch current GPS coordinates and populate.
   */
  const handleUseCurrentLocation = async () => {
    setAcquiringGps(true);
    try {
      // 1. Verify if device GPS/Location service is enabled
      const isGpsEnabled = await checkLocationServicesEnabled();
      if (!isGpsEnabled) {
        CustomAlert.alert(
          "GPS Disabled",
          "Device location services are turned off. Please enable GPS / Location in your device settings to use this feature.",
          [
            { text: "Cancel", style: "cancel" },
            {
              text: "Open Settings",
              style: "default",
              onPress: () => {
                if (Platform.OS !== "web") {
                  Linking.openSettings();
                }
              },
            },
          ]
        );
        return;
      }

      // 2. Verify and request permissions if needed
      const perm = await checkLocationPermissions();
      if (!perm.granted) {
        const req = await requestLocationPermissions(true);
        if (!req.granted) {
          CustomAlert.alert(
            "Permission Required",
            "Location permissions are required to acquire your current GPS coordinates. Please grant location permissions in device settings.",
            [
              { text: "Cancel", style: "cancel" },
              {
                text: "Open Settings",
                style: "default",
                onPress: () => {
                  if (Platform.OS !== "web") {
                    Linking.openSettings();
                  }
                },
              },
            ]
          );
          return;
        }
      }

      // 3. Acquire high-accuracy position
      const loc = await getHighAccuracyLocation();
      if (loc) {
        setLatitude(loc.latitude);
        setLongitude(loc.longitude);
        setLatInput(String(loc.latitude));
        setLonInput(String(loc.longitude));

        const friendly = await reverseGeocode(loc.latitude, loc.longitude);
        setLocationName(friendly || loc.label);
      } else {
        CustomAlert.alert(
          "GPS Unavailable",
          "Could not acquire your current location. Please verify that GPS has a clear signal and permissions are enabled."
        );
      }
    } catch (err: any) {
      CustomAlert.alert("GPS Error", err.message || "Failed to read GPS coordinates");
    } finally {
      setAcquiringGps(false);
    }
  };

  /**
   * Update coordinates when manually changing latitude/longitude inputs.
   */
  const handleLatBlur = () => {
    const val = parseFloat(latInput);
    if (!isNaN(val) && val >= -90 && val <= 90) {
      setLatitude(val);
    }
  };

  const handleLonBlur = () => {
    const val = parseFloat(lonInput);
    if (!isNaN(val) && val >= -180 && val <= 180) {
      setLongitude(val);
    }
  };

  /**
   * Open the current coordinates directly in Google Maps application or website.
   */
  const handleOpenInGoogleMaps = () => {
    if (latitude == null || longitude == null) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    Linking.openURL(url).catch((err) => {
      console.warn("Could not open Google Maps link:", err);
    });
  };

  /**
   * Apply selected location and coordinates.
   */
  const handleConfirm = () => {
    if (latitude == null || longitude == null) {
      CustomAlert.alert("Select a location", "Please search for a place, use GPS, or enter coordinates.");
      return;
    }

    const trimmedName = locationName.trim();
    const latNum = Number(latitude);
    const lonNum = Number(longitude);
    const finalName =
      trimmedName ||
      (!isNaN(latNum) && !isNaN(lonNum)
        ? `${latNum.toFixed(4)}, ${lonNum.toFixed(4)}`
        : "Selected Location");

    onSelect({
      location: finalName,
      latitude,
      longitude,
      isApproximate,
    });
    onClose();
  };

  const hasCoords = latitude != null && longitude != null;

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title="Google Maps Location"
      icon="map-outline"
      width={layout.width}
      maxHeight={layout.maxHeight}
      animationType="fade"
      cardStyle={{ backgroundColor: cardBg, borderColor }}
      testID="google-maps-location"
      footer={
        <View style={styles.modalFooter}>
          <ThemedButton title="Cancel" variant="minimal" onPress={onClose} style={styles.footerBtn} />
          <ThemedButton
            title="Apply Location"
            variant="primary"
            disabled={!hasCoords}
            onPress={handleConfirm}
            style={styles.footerBtn}
          />
        </View>
      }
    >
      <ScrollView style={styles.modalBody} keyboardShouldPersistTaps="handled">
        {/* Search and GPS Bar */}
        <ThemedText style={styles.sectionSubtitle}>Search place, address, or paste Google Maps link</ThemedText>
        <View style={styles.searchRow}>
          <View style={{ flex: 1 }}>
            <ThemedInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="e.g. Eiffel Tower, Paris or coordinates"
              style={styles.searchInput}
              returnKeyType="search"
              onSubmitEditing={handleSearch}
            />
          </View>
          <ThemedButton
            title={searching ? "" : "Search"}
            onPress={handleSearch}
            loading={searching}
            style={styles.searchBtn}
            variant="primary"
          />
        </View>

        {/* GPS Trigger Button */}
        <View style={styles.quickActionsRow}>
          <TouchableOpacity
            style={[styles.quickActionBtn, { backgroundColor: badgeBg }]}
            onPress={handleUseCurrentLocation}
            disabled={acquiringGps}
          >
            {acquiringGps ? (
              <ActivityIndicator size="small" color={tintColor} style={{ marginRight: 6 }} />
            ) : (
              <Ionicons name="navigate-circle-outline" size={16} color={tintColor} style={{ marginRight: 6 }} />
            )}
            <ThemedText style={styles.quickActionText}>
              {acquiringGps ? "Acquiring GPS..." : "Use Current Location"}
            </ThemedText>
          </TouchableOpacity>
        </View>

        {/* Search Results List */}
        {searchResults.length > 0 && (
          <View style={[styles.resultsContainer, { borderColor, backgroundColor: screenBg }]}>
            <ThemedText style={styles.resultsHeader}>Select from search results:</ThemedText>
            {searchResults.map((place) => (
              <TouchableOpacity
                key={place.place_id}
                style={[styles.resultItem, { borderBottomColor: borderColor }]}
                onPress={() => handleSelectPlace(place)}
              >
                <Ionicons name="location-sharp" size={16} color={tintColor} style={{ marginRight: 8, marginTop: 2 }} />
                <View style={{ flex: 1 }}>
                  <ThemedText style={styles.resultTitle} numberOfLines={2}>
                    {place.display_name}
                  </ThemedText>
                  <ThemedText style={[styles.resultCoords, { color: textMuted }]}>
                    {parseFloat(place.lat).toFixed(4)}, {parseFloat(place.lon).toFixed(4)}
                  </ThemedText>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Map Preview Area */}
        <View style={styles.mapSection}>
          {hasCoords ? (
            <View style={styles.mapContainer}>
              <GoogleMapsEmbed latitude={latitude!} longitude={longitude!} />
              <TouchableOpacity
                style={[styles.externalLinkBtn, { backgroundColor: cardBg }]}
                onPress={handleOpenInGoogleMaps}
                accessibilityLabel="Open in Google Maps application"
              >
                <Ionicons name="open-outline" size={14} color={tintColor} style={{ marginRight: 4 }} />
                <ThemedText style={[styles.externalLinkText, { color: tintColor }]}>
                  Open in Google Maps
                </ThemedText>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={[styles.emptyMapPlaceholder, { backgroundColor: badgeBg, borderColor }]}>
              <Ionicons name="map-outline" size={24} color={textMuted} style={{ marginBottom: 2 }} />
              <ThemedText style={[styles.emptyMapText, { color: textMuted }]}>
                No location selected yet.
              </ThemedText>
              <ThemedText style={[styles.emptyMapSubText, { color: textMuted }]}>
                Search for an address or use your current location to preview the Google Maps pin.
              </ThemedText>
            </View>
          )}
        </View>

        {/* Selected Location Details Form */}
        <View style={styles.detailsSection}>
          <ThemedText style={styles.inputLabel}>Location Name</ThemedText>
          <ThemedInput
            value={locationName}
            onChangeText={setLocationName}
            placeholder="e.g. Sydney Opera House"
            style={styles.detailInput}
          />

          <View style={styles.coordsRow}>
            <View style={{ flex: 1, marginRight: 8 }}>
              <ThemedText style={styles.inputLabel}>Latitude</ThemedText>
              <ThemedInput
                value={latInput}
                onChangeText={setLatInput}
                onBlur={handleLatBlur}
                placeholder="-90 to 90"
                keyboardType="numeric"
                style={styles.coordInput}
              />
            </View>
            <View style={{ flex: 1, marginLeft: 8 }}>
              <ThemedText style={styles.inputLabel}>Longitude</ThemedText>
              <ThemedInput
                value={lonInput}
                onChangeText={setLonInput}
                onBlur={handleLonBlur}
                placeholder="-180 to 180"
                keyboardType="numeric"
                style={styles.coordInput}
              />
            </View>
          </View>

          <View style={styles.approxRow}>
            <ThemedToggle
              value={isApproximate}
              onValueChange={setIsApproximate}
              label="Approximate location"
              sublabel="Show that this location is an approximation"
              testID="location-approximate-toggle"
            />
          </View>
        </View>
      </ScrollView>
    </ThemedPopupScreen>
  );
}

const styles = StyleSheet.create({
  hoverCard: {
    position: "absolute",
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 12,
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "700",
  },
  closeBtn: {
    padding: 4,
  },
  modalBody: {
    padding: 7,
    flexShrink: 1,
  },
  sectionSubtitle: {
    fontSize: 11,
    fontWeight: "600",
    marginBottom: 4,
    opacity: 0.8,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  searchInput: {
    fontSize: 13,
  },
  searchBtn: {
    minWidth: 64,
  },
  quickActionsRow: {
    flexDirection: "row",
    marginTop: 4,
    marginBottom: 6,
  },
  quickActionBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  quickActionText: {
    fontSize: 11,
    fontWeight: "600",
  },
  resultsContainer: {
    borderRadius: 6,
    borderWidth: 1,
    marginBottom: 6,
    overflow: "hidden",
  },
  resultsHeader: {
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
    paddingHorizontal: 8,
    paddingVertical: 4,
    opacity: 0.7,
  },
  resultItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderBottomWidth: 1,
  },
  resultTitle: {
    fontSize: 12,
    fontWeight: "500",
  },
  resultCoords: {
    fontSize: 10,
    marginTop: 1,
  },
  mapSection: {
    marginVertical: 6,
  },
  mapContainer: {
    width: "100%",
    height: 120,
    borderRadius: 6,
    overflow: "hidden",
    position: "relative",
  },
  externalLinkBtn: {
    position: "absolute",
    bottom: 6,
    right: 6,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  externalLinkText: {
    fontSize: 10,
    fontWeight: "600",
  },
  emptyMapPlaceholder: {
    height: 90,
    borderRadius: 6,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  emptyMapText: {
    fontSize: 11.5,
    fontWeight: "600",
    lineHeight: 15,
    marginBottom: 1,
  },
  emptyMapSubText: {
    fontSize: 9.5,
    lineHeight: 13,
    textAlign: "center",
    paddingHorizontal: 4,
  },
  detailsSection: {
    marginTop: 4,
    gap: 6,
  },
  inputLabel: {
    fontSize: 11,
    fontWeight: "600",
    marginBottom: 2,
    opacity: 0.8,
  },
  detailInput: {
    fontSize: 13,
  },
  coordsRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  coordInput: {
    fontSize: 12,
  },
  approxRow: {
    marginTop: 6,
    marginBottom: 2,
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderTopWidth: 1,
    gap: 8,
  },
  footerBtn: {
    minWidth: 80,
  },
});

/**
 * Standardized LocationSelector export.
 * Popups use the Popup UX pattern.
 */
export const LocationSelector = GoogleMapsLocationSelector;
export default GoogleMapsLocationSelector;
